/**
 * Deck — the turntable of «Сейчас играет»: the drawing of an SL-1210MK2's top plate, its
 * platter, the record, the tonearm and the keys.
 *
 * Task 20. `spec.md` «Пластинка», «Дека». The picture of the plate is `assets/deck.svg`; over it
 * lie the parts that turn, light up or are pressed, each at its place in the plate's
 * millimetres (`styles/deck.css`). The maker's name is nowhere — not in the drawing, not in a
 * label for a screen reader: the project's is in its place.
 *
 * ## What each part does
 *
 * | part | what it does |
 * |---|---|
 * | the platter | turns with the sound, under a held record too, as a platter under a slipmat; its strobe dots light up as they pass the lamp |
 * | the record | `components/Vinyl.tsx`: a finger anywhere on the platter holds it — the scratch of task 18, or the table of task 09 |
 * | the tonearm | `components/Tonearm.tsx`: its needle in the groove of the second playing |
 * | START·STOP | plays and pauses, exactly as the big «играть» |
 * | 33 and 45 | task 21: the big record or the small one with the flag adapter in its middle — the sound is the same, the big one turns slower; the choice is remembered on the phone (`store/deck.ts`); not while a hand is on the deck |
 * | the adapter's holder | at 33 the adapter waits in it; at 45 it is on the record — the one that suits the label (`lib/labels.ts`) |
 * | the pitch | task 23: −8 … +8 %, down is plus as on the deck, a click into zero near zero; by finger, by keyboard, and a screen reader hears a slider with its value. The sound goes faster or slower and its tone with it; remembered on the phone beside the record since task 48 |
 *
 * ## The face of the record
 *
 * The label on the record — one of twelve, or the track's cover (`lib/labels.ts`) — is drawn at
 * random when the deck opens, and again at each tap on the record (`components/Vinyl.tsx`). The
 * deck keeps it, not the record: its holder shows the adapter that suits it. A deck's life is the
 * screen's, so a change of track keeps the face and only opening the screen again draws anew.
 * Since 2026-11-04 the face is the active deck's in `store/deck.ts`, not this component's: this
 * turntable shows the active deck's record (size and pitch too), and the pult's platter of that
 * deck shows the same record and taps it too (`components/PultBoard.tsx`), so a tap on either is
 * seen on both. Which deck is active may change while this is open, and the face follows it.
 *
 * ## START·STOP is a switch, not a second «Играть»
 *
 * A second button called «Играть» on the same screen would make two of them for a screen reader
 * and for a finger that looks for the big one. So the key keeps its own name and says whether
 * the sound goes by being pressed or not (`aria-pressed`) — which is what the deck's key is.
 *
 * ## The keys are felt (task 50)
 *
 * START·STOP, 33 and 45 give a light knock of Telegram's vibration, and the tempo a tick when it
 * clicks into zero (`lib/telegram.ts`, `haptic`). Nothing at all where the phone cannot.
 */

import type { CSSProperties, JSX, KeyboardEvent, PointerEvent } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import deckUrl from '../assets/deck.svg';
import platterLitUrl from '../assets/platter-lit.svg';
import platterUrl from '../assets/platter.svg';
import { player } from '../engine/player';
import { formatPitch } from '../lib/format';
import { adapterFor, nextFace } from '../lib/labels';
import type { RecordFace } from '../lib/labels';
import { telegram } from '../lib/telegram';
import { EVERY_DECK, PITCH_RANGE, chooseRecord, setFace, setPitch, useDeck } from '../store/deck';
import '../styles/deck.css';

import { usePlaybackFlags } from './PlayerControls';
import { Tonearm } from './Tonearm';
import { Vinyl } from './Vinyl';
import { ADAPTER_ART } from './labelArt';
import { useDeckTurn, useReducedMotion } from './motion';

export const DECK_LABEL = 'Проигрыватель';
export const START_STOP_LABEL = 'Старт-стоп';
export const SPEED_33_LABEL = '33 оборота — большая пластинка';
export const SPEED_45_LABEL = '45 оборотов — маленькая пластинка';
export const PITCH_LABEL = 'Темп';

/** One press of an arrow key on the pitch slider, in percent; a page key moves two. */
export const PITCH_KEY_STEP = 0.5;

export interface DeckProps {
  /** A cover URL from the server, or `null` for the label «Ямайская» (task 24). */
  readonly cover: string | null;
  /** The track's title and performer, for the label. */
  readonly title: string;
  readonly performer: string;
  /** The sound is going: the platter and the record turn. */
  readonly spinning: boolean;
}

/** The pitch plate, in millimetres: the knob's centre travels 100 mm from 5 mm below its top. */
const PLATE_MM = { height: 117, travelFrom: 5, travel: 100 } as const;

/** The pitch knob's centre on its plate, in millimetres from the plate's top, at a tempo. */
function knobAt(percent: number): number {
  return PLATE_MM.travelFrom + ((percent + PITCH_RANGE) / (2 * PITCH_RANGE)) * PLATE_MM.travel;
}

/**
 * The pitch slider of the deck (task 23). A finger anywhere on the plate puts the knob there
 * and drags it; down is plus, as printed on the plate. Every value goes through `setPitch`
 * (`store/deck.ts`), which clicks it into zero near zero; the player makes it the element's
 * speed and writes it no oftener than every 50 ms (`engine/speed.ts`).
 *
 * The keys follow the value, not the plate: up and right are plus, as for every slider. A screen
 * reader's «increase» is a slider's «up», and a plate-wise mapping would have it slow the track
 * down (found by the review of tasks 18–24); on the plate the knob then moves down, as it must
 * for a plus.
 */
function PitchSlider(): JSX.Element {
  const pitch = useDeck((s) => s.pitch);
  const dragging = useRef<number | undefined>(undefined);

  /** Every move of the knob. Task 50: the click into zero is felt, like a real slider's detent. */
  function movePitch(percent: number): void {
    const before = useDeck.getState().pitch;
    // Task 55: the turntable sets both decks — `store/deck.ts`, «The turntable is one deck».
    setPitch(percent, EVERY_DECK);
    if (before !== 0 && useDeck.getState().pitch === 0) telegram.haptic('selection');
  }

  function toPointer(event: PointerEvent<HTMLDivElement>): void {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.height <= 0) return;
    const mm = ((event.clientY - box.top) / box.height) * PLATE_MM.height;
    const share = Math.min(1, Math.max(0, (mm - PLATE_MM.travelFrom) / PLATE_MM.travel));
    movePitch(-PITCH_RANGE + 2 * PITCH_RANGE * share);
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>): void {
    if (dragging.current !== undefined || event.isPrimary === false || event.button !== 0) return;
    dragging.current = event.pointerId;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Without pointer capture the knob follows while the finger stays on the plate.
    }
    toPointer(event);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>): void {
    if (dragging.current === event.pointerId) toPointer(event);
  }

  function onPointerEnd(event: PointerEvent<HTMLDivElement>): void {
    if (dragging.current === event.pointerId) dragging.current = undefined;
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const now = useDeck.getState().pitch;
    const next: Record<string, number> = {
      ArrowUp: now + PITCH_KEY_STEP,
      ArrowRight: now + PITCH_KEY_STEP,
      ArrowDown: now - PITCH_KEY_STEP,
      ArrowLeft: now - PITCH_KEY_STEP,
      PageUp: now + 2 * PITCH_KEY_STEP,
      PageDown: now - 2 * PITCH_KEY_STEP,
      Home: -PITCH_RANGE,
      End: PITCH_RANGE,
      '0': 0,
    };
    const to = next[event.key];
    if (to === undefined) return;
    event.preventDefault();
    movePitch(to);
  }

  return (
    <div
      className="deckPart deckPitch"
      role="slider"
      tabIndex={0}
      aria-label={PITCH_LABEL}
      aria-orientation="vertical"
      aria-valuemin={-PITCH_RANGE}
      aria-valuemax={PITCH_RANGE}
      aria-valuenow={pitch}
      aria-valuetext={formatPitch(pitch)}
      style={{ '--knob': knobAt(pitch) } as CSSProperties}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onLostPointerCapture={onPointerEnd}
      onKeyDown={onKeyDown}
      onDoubleClick={() => {
        movePitch(0);
      }}
    >
      <span className="deckPitchLed" data-on={pitch === 0 ? 'true' : 'false'} />
      <span className="deckPitchKnob" />
    </div>
  );
}

export function Deck({ cover, title, performer, spinning }: DeckProps): JSX.Element {
  const reducedMotion = useReducedMotion();
  const { playing } = usePlaybackFlags();
  const size = useDeck((s) => s.size);
  const pitch = useDeck((s) => s.pitch);
  const turning = spinning && !reducedMotion ? 'true' : 'false';
  const platter = useRef<HTMLImageElement | null>(null);
  const lit = useRef<HTMLImageElement | null>(null);
  const motor = useMemo(() => [platter, lit], []);
  // The motor runs at the record's speed, 33⅓ or 45, and at the tempo's pace.
  useDeckTurn(motor, size, pitch);
  // The face drawn as this deck opens, once, into the deck active then; the active deck's from
  // then on (see «The face of the record»).
  const [opened] = useState<RecordFace>(() => nextFace(cover !== null, undefined));
  useLayoutEffect(() => {
    setFace(useDeck.getState().active, opened);
  }, [opened]);
  const face = useDeck((s) => s.decks[s.active].face);
  const adapter = adapterFor(face);

  return (
    <div className="deck" role="group" aria-label={DECK_LABEL}>
      <img className="deckPart deckArt" src={deckUrl} alt="" draggable={false} />
      <img
        className="deckPart deckAdapter"
        src={ADAPTER_ART[adapter]}
        data-adapter={adapter}
        alt=""
        draggable={false}
        data-shown={size === '33' ? 'true' : 'false'}
      />
      <div className="deckPart deckPlatter" aria-hidden="true">
        <img
          ref={platter}
          className="deckPlatterSpin"
          src={platterUrl}
          alt=""
          data-size={size}
          data-spinning={turning}
        />
      </div>
      <div className="deckPart deckStrobe" aria-hidden="true">
        <img
          ref={lit}
          className="deckPlatterSpin"
          src={platterLitUrl}
          alt=""
          data-size={size}
          data-spinning={turning}
        />
      </div>
      <Vinyl
        cover={cover}
        title={title}
        performer={performer}
        spinning={spinning}
        size={size}
        face={face}
        onTap={() => {
          const { active, decks } = useDeck.getState();
          setFace(active, nextFace(cover !== null, decks[active].face));
        }}
      />
      <Tonearm size={size} />
      <button
        type="button"
        className="deckPart deckStart"
        aria-label={START_STOP_LABEL}
        aria-pressed={playing}
        onClick={() => {
          telegram.haptic('impact');
          player().toggle();
        }}
      >
        <span className="deckStartCap" aria-hidden="true">
          start · stop
        </span>
      </button>
      <button
        type="button"
        className="deckPart deckSpeed deck33"
        aria-label={SPEED_33_LABEL}
        aria-pressed={size === '33'}
        onClick={() => {
          telegram.haptic('impact');
          chooseRecord('33', EVERY_DECK);
        }}
      >
        33
      </button>
      <button
        type="button"
        className="deckPart deckSpeed deck45"
        aria-label={SPEED_45_LABEL}
        aria-pressed={size === '45'}
        onClick={() => {
          telegram.haptic('impact');
          chooseRecord('45', EVERY_DECK);
        }}
      >
        45
      </button>
      <PitchSlider />
    </div>
  );
}
